/**
 * Database-Driven Treatment Mapping Utility
 * 
 * Maps AI-detected dental problems to treatment recommendations using
 * real database data from getTreatDiagList and getTreatList with NHSO compliance
 */

/**
 * AI detection to K-code diagnosis mapping with priority selection
 * Maps AI-detected problems to specific ICD-10 K-codes from database
 */
const AI_DETECTION_KEYWORDS = {
  'เหงือกอักเสบ': {
    keywords: ['เหงือก', 'อักเสบ', 'gingivitis'],
    primaryKCode: 'K0509', // Chronic gingivitis, unspecified
    alternateKCodes: ['K0519', 'K0510']
  },
  'ฟันผุ': {
    keywords: ['ฟันผุ', 'ผุ', 'caries', 'cavity', 'dental caries'],
    primaryKCode: 'K021', // Caries of dentine
    alternateKCodes: ['K020', 'K029']
  },
  'คราบหินปูน': {
    keywords: ['หินปูน', 'คราบ', 'calculus', 'tartar', 'plaque'],
    primaryKCode: 'K0366', // Supragingival calculus
    alternateKCodes: ['K0364', 'K0365']
  },
  'ฟันสึก': {
    keywords: ['ฟัน', 'สึก', 'attrition', 'worn'],
    primaryKCode: 'K0309', // Pathological resorption of teeth, unspecified
    alternateKCodes: ['K0300']
  },
  'คอฟันสึก': {
    keywords: ['คอฟัน', 'สึก', 'abrasion'],
    primaryKCode: 'K0311', // Abrasion of teeth, limited to enamel
    alternateKCodes: ['K0319', 'K0310']
  },
  'ฟันตกกระ': {
    keywords: ['ตกกระ', 'fluorosis', 'mottled'],
    primaryKCode: 'K0030', // Mottled teeth
    alternateKCodes: []
  }
};

/**
 * Priority mappings for different confidence levels
 */
const CONFIDENCE_PRIORITY_MAP = {
  high: 0.8,    // > 80% confidence
  medium: 0.6,  // 60-80% confidence
  low: 0.4      // 40-60% confidence
};

/**
 * Fallback treatments for cases where database lookup fails
 */
const FALLBACK_TREATMENTS = {
  'เหงือกอักเสบ': { treatment: 'ขูดหินปูน', price: 1200, priority: 'high' },
  'ฟันผุ': { treatment: 'อุดฟัน', price: 2500, priority: 'high' },
  'คราบหินปูน': { treatment: 'ขูดหินปูน', price: 1000, priority: 'medium' },
  'ลิ้นแผนที่': { treatment: 'สังเกตอาการ', price: 500, priority: 'low' },
  'คอฟันสึก': { treatment: 'อุดฟันบริเวณคอฟัน', price: 2500, priority: 'medium' },
  'ฟันสึก': { treatment: 'แผ่นกัดฟัน', price: 3000, priority: 'medium' },
  'ฟันตกกระ': { treatment: 'ฟอกสีฟัน', price: 8000, priority: 'low' }
};

// Removed hardcoded mappings - now uses dynamic database lookup

/**
 * Database-Driven Treatment Mapping Class
 */
class TreatmentMapping {
  
  /**
   * Convert AI detections to treatment plan recommendations using database
   * @param {Object} detections - AI analysis results
   * @param {Object} options - Mapping options
   * @param {Object} store - Vuex store for database access
   * @returns {Array} Treatment plan items
   */
  static async generateTreatmentPlan(detections, options = {}, store) {
    const {
      includePreventive = true,
      maxTreatmentsPerProblem = 2,
      minConfidence = 0.3,
      prioritizeByConfidence = true
    } = options;

    const treatmentPlan = [];
    let planId = 1;

    try {
      // Process each detection category
      for (const category of Object.keys(detections)) {
        const categoryDetections = detections[category] || [];
        for (let detectionIndex = 0; detectionIndex < categoryDetections.length; detectionIndex++) {
          const detection = categoryDetections[detectionIndex];
          
          // Skip low confidence detections
          if (detection.confidence < minConfidence) {
            continue;
          }
          
          try {
            // Step 1: Find diagnosis from database (ICD-10 K-codes only)
            const diagnosisList = await this.findDiagnosisForProblem(detection.class_name, store);
            
            // Skip detections without valid ICD-10 diagnoses
            if (diagnosisList.length === 0) {
              console.warn(`[DIAGNOSIS-ONLY] No ICD-10 diagnoses found for: ${detection.class_name}, skipping`);
              continue;
            }
            
            for (let diagnosisIndex = 0; diagnosisIndex < diagnosisList.length; diagnosisIndex++) {
              const diagnosis = diagnosisList[diagnosisIndex];
              
              // Validate that this diagnosis has a proper K-code
              if (!diagnosis.code || !diagnosis.code.startsWith('K')) {
                console.warn(`[DIAGNOSIS-ONLY] Diagnosis ${diagnosis.nameTh} does not have valid K-code: ${diagnosis.code}, skipping`);
                continue;
              }
              
              // Generate unique spatial identifier for multiple detections
              const spatialId = this.generateSpatialId(detection, category, detectionIndex);
              const locationLabel = this.generateLocationLabel(detection, detectionIndex);

              // Step 2: Create diagnosis-only plan item (NO treatment lookup)
              const diagnosisPlanItem = {
                // Unique identifiers - include spatial coordinates for uniqueness
                id: `ai_diagnosis_${category}_${detectionIndex}_${diagnosisIndex}_${spatialId}`,
                planId: planId++,
                spatialId: spatialId,              // Unique spatial identifier

                // Tooth information - AI doesn't provide this
                toothNo: null,
                toothSurfaces: [],

                // Database references (DIAGNOSIS-ONLY)
                diagnosis: diagnosis.id,           // Database diagnosis ID with K-code
                treatment: null,                   // NO treatment mapping (diagnosis-only)

                // Pricing (not applicable for diagnosis-only, set to 0)
                price: 0,
                originalPrice: 0,

                // Descriptions (DIAGNOSIS-focused) - include location info
                detail: `${diagnosis.nameTh || diagnosis.name || 'การวินิจฉัย'} ${locationLabel}`,
                diagnosisName: `${diagnosis.nameTh || diagnosis.name || 'การวินิจฉัย'} ${locationLabel}`,
                treatmentName: null,               // NO treatment name (diagnosis-only)

                // Status and workflow
                status: 'Diagnosed',               // Different status for diagnosis

                // AI metadata - enhanced for multiple detections
                aiGenerated: true,
                aiConfidence: detection.confidence,
                aiProblemType: detection.class_name,
                aiPhotoType: detection.photoType,
                aiBoundingBox: detection.box,
                aiDetectionArea: this.calculateDetectionArea(detection.box),
                aiLocationLabel: locationLabel,    // Human-readable location

                // Medical codes (DIAGNOSIS-ONLY)
                diagnosisCode: diagnosis.code,      // K-code (guaranteed to exist)
                treatmentCode: null,                // NO treatment code (diagnosis-only)

                // Priority and ordering
                priority: this.calculateDiagnosisPriority(diagnosis, detection),
                order: planId - 1,

                // Additional metadata for multiple detections
                category: category,
                detectionIndex: detectionIndex,
                diagnosisIndex: diagnosisIndex,
                estimatedDuration: 0,               // Not applicable for diagnosis
                requiresFollowup: true,             // Diagnosis usually requires follow-up
                isMultipleDetection: true,          // Flag for multiple detection handling

                // Clinical notes (DIAGNOSIS-focused)
                clinicalNotes: this.generateDiagnosisClinicalNotes(detection, diagnosis),

                // Flags
                isDiagnosisOnly: true,              // Flag to identify diagnosis-only items
                hasValidKCode: true                 // Confirmed K-code validation
              };
              
              treatmentPlan.push(diagnosisPlanItem);
            }
          } catch (diagnosisError) {
            console.error(`[DIAGNOSIS-ONLY] Error finding diagnosis for problem ${detection.class_name}:`, diagnosisError);
            
            // NO FALLBACK for diagnosis-only mode - we only want valid ICD-10 diagnoses
            console.warn(`[DIAGNOSIS-ONLY] Skipping ${detection.class_name} - no valid ICD-10 diagnosis found`);
          }
        }
      }

      // Step 5: Post-processing and optimization
      const optimizedPlan = this.optimizeTreatmentPlan(treatmentPlan, options);

      return optimizedPlan;
      
    } catch (error) {
      console.error('Treatment plan generation failed:', error);
      
      // Fallback to basic treatment plan
      return this.generateFallbackTreatmentPlan(detections, options);
    }
  }

  /**
   * Find diagnosis from database for AI-detected problem using K-code priority mapping
   * @param {string} problemName - Thai problem name from AI
   * @param {Object} store - Vuex store
   * @returns {Array} Matching diagnosis records (diagnosis-only mapping)
   */
  static async findDiagnosisForProblem(problemName, store) {
    try {
      if (!store) {
        throw new Error('Store not provided for database access');
      }
      
      const diagnosisList = store.getters['moduleAppointment/getTreatDiagList'] || [];
      
      // Get K-code mapping for this AI detection
      const detectionMapping = AI_DETECTION_KEYWORDS[problemName];
      if (!detectionMapping) {
        console.warn(`No K-code mapping found for problem: ${problemName}`);
        return [];
      }
      
      const { keywords, primaryKCode, alternateKCodes } = detectionMapping;
      
      // Priority-based K-code search strategies
      const kCodeSearchStrategies = [
        // 1. Primary K-code exact match (highest priority)
        diagnosis => diagnosis.code === primaryKCode,
        
        // 2. Alternate K-codes exact match
        diagnosis => alternateKCodes.includes(diagnosis.code),
        
        // 3. Keyword match with K-code validation (fallback)
        diagnosis => {
          if (!diagnosis.nameTh) return false;
          const hasKeywordMatch = keywords.some(keyword => 
            diagnosis.nameTh.includes(keyword) || 
            (diagnosis.nameEn && diagnosis.nameEn.toLowerCase().includes(keyword.toLowerCase()))
          );
          // Also check if it's a valid K-code
          return hasKeywordMatch && diagnosis.code && diagnosis.code.startsWith('K');
        }
      ];
      
      let matchingDiagnosis = [];
      for (let strategyIndex = 0; strategyIndex < kCodeSearchStrategies.length; strategyIndex++) {
        const strategy = kCodeSearchStrategies[strategyIndex];
        matchingDiagnosis = diagnosisList.filter(strategy);
        
        if (matchingDiagnosis.length > 0) {
          // For primary K-code match, take only the first result
          if (strategyIndex === 0) {
            matchingDiagnosis = matchingDiagnosis.slice(0, 1);
          }
          // For alternate K-codes, prefer first alternate
          else if (strategyIndex === 1) {
            // Sort by alternateKCodes order preference
            matchingDiagnosis.sort((a, b) => {
              const aIndex = alternateKCodes.indexOf(a.code);
              const bIndex = alternateKCodes.indexOf(b.code);
              return aIndex - bIndex;
            });
            matchingDiagnosis = matchingDiagnosis.slice(0, 1);
          }
          break;
        }
      }
      
      if (matchingDiagnosis.length === 0) {
        console.warn(`[DIAGNOSIS-ONLY] No K-code diagnosis found for problem: ${problemName}`);
        console.warn(`[DIAGNOSIS-ONLY] Expected K-codes: ${primaryKCode}, ${alternateKCodes.join(', ')}`);
      }
      
      const result = matchingDiagnosis.map(d => ({ 
        id: d.id, 
        name: d.nameTh, 
        nameEn: d.nameEn,
        code: d.code,
        aiMappingType: problemName,
        isKCodeMatch: true
      }));
      
      return matchingDiagnosis;
      
    } catch (error) {
      console.error('[DIAGNOSIS-ONLY] Error finding diagnosis:', error);
      return [];
    }
  }
  
  /**
   * Find treatments from database for a diagnosis
   * @param {number} diagnosisId - Database diagnosis ID
   * @param {Object} store - Vuex store
   * @param {Object} detection - Detection data for context
   * @returns {Array} Available treatments
   */
  static async findTreatmentsForDiagnosis(diagnosisId, store, detection) {
    try {
      if (!store) {
        throw new Error('Store not provided for database access');
      }
      
      const treatList = store.getters['moduleAppointment/getTreatList'] || [];
      const nhsoIds = store.getters['moduleAppointment/getNhsoMtreatIds'] || [];
      const diagnosis = store.getters['moduleAppointment/getTreatDiagBy'](diagnosisId, 'id');
      
      
      // Multiple relationship strategies
      const relationshipStrategies = [
        // Direct relationship (if treatment has diagnosisId field)
        treatment => treatment.diagnosisId === diagnosisId,
        // Related diagnosis array (if treatment has multiple related diagnoses)
        treatment => treatment.relatedDiagnosis && treatment.relatedDiagnosis.includes(diagnosisId),
        // Category-based relationship (if both have category codes)
        treatment => {
          if (!treatment.categoryId || !diagnosis) return false;
          return this.getDiagnosisCategory(diagnosisId, store) === treatment.categoryId;
        },
        // Keyword-based relationship (fallback)
        treatment => {
          if (!diagnosis || !diagnosis.nameTh || !treatment.nameTh) return false;
          const diagnosisKeywords = diagnosis.nameTh.split(' ');
          const treatmentText = `${treatment.nameTh} ${treatment.nameEn || ''}`.toLowerCase();
          return diagnosisKeywords.some(keyword => 
            treatmentText.includes(keyword.toLowerCase()) ||
            keyword.includes(treatment.nameTh.substring(0, 2)) // Partial match
          );
        },
        // Broad category match (very fallback)
        treatment => {
          if (!treatment.nameTh) return false;
          const problemType = detection.class_name;
          if (problemType.includes('เหงือก') && treatment.nameTh.includes('หินปูน')) return true;
          if (problemType.includes('ฟันผุ') && (treatment.nameTh.includes('อุด') || treatment.nameTh.includes('รักษา'))) return true;
          if (problemType.includes('หินปูน') && treatment.nameTh.includes('หินปูน')) return true;
          return false;
        }
      ];
      
      let availableTreatments = [];
      for (let i = 0; i < relationshipStrategies.length; i++) {
        const strategy = relationshipStrategies[i];
        availableTreatments = treatList.filter(treatment => {
          const isNhsoApproved = nhsoIds.includes(treatment.id);
          const isRelated = strategy(treatment);
          return isNhsoApproved && isRelated;
        });
        
        if (availableTreatments.length > 0) break;
      }
      
      // If no NHSO treatments found, try without NHSO restriction but log warning
      if (availableTreatments.length === 0) {
        console.warn('No NHSO-approved treatments found, searching all treatments...');
        for (const strategy of relationshipStrategies) {
          availableTreatments = treatList.filter(strategy);
          if (availableTreatments.length > 0) {
            console.warn(`Found ${availableTreatments.length} non-NHSO treatments`);
            break;
          }
        }
      }
      
      return availableTreatments;
      
    } catch (error) {
      console.error('Error finding treatments:', error);
      return [];
    }
  }

  // Removed estimateToothNumber and estimateToothSurfaces methods
  // AI doesn't provide tooth number or surface information

  /**
   * Filter treatments by AI confidence level
   * @param {Array} treatments - Available treatments
   * @param {Object} detection - Detection data
   * @returns {Array} Filtered treatments
   */
  static filterTreatmentsByConfidence(treatments, detection) {
    return treatments.filter(treatment => {
      if (detection.confidence > CONFIDENCE_PRIORITY_MAP.high) {
        return true; // High confidence - all treatments
      }
      if (detection.confidence > CONFIDENCE_PRIORITY_MAP.medium) {
        // Medium confidence - exclude experimental/optional treatments
        return !treatment.isOptional && !treatment.isExperimental;
      }
      // Low confidence - only essential treatments
      return treatment.isEssential || treatment.priority === 'high' || 
             (treatment.nameTh && (treatment.nameTh.includes('ขูด') || treatment.nameTh.includes('อุด')));
    });
  }
  
  /**
   * Calculate adjusted price based on confidence and complexity
   * @param {Object} treatment - Treatment data from database
   * @param {Object} detection - Detection data
   * @returns {number} Calculated price
   */
  static calculateAdjustedPrice(treatment, detection) {
    let basePrice = treatment.price || 0;
    
    // If no price in database, use fallback estimation
    if (basePrice === 0) {
      const fallback = FALLBACK_TREATMENTS[detection.class_name];
      basePrice = fallback ? fallback.price : 1000;
    }
    
    // Adjust price based on confidence (higher confidence = standard price)
    if (detection.confidence < 0.5) {
      basePrice *= 0.8; // 20% discount for uncertain cases
    } else if (detection.confidence > 0.9) {
      basePrice *= 1.1; // 10% premium for very certain cases
    }
    
    // Complexity adjustment based on bounding box size
    if (detection.box && detection.box.length >= 4) {
      const [x1, y1, x2, y2] = detection.box;
      const area = (x2 - x1) * (y2 - y1);
      if (area > 10000) { // Large affected area
        basePrice *= 1.2; // 20% increase for complex cases
      }
    }
    
    return Math.round(basePrice);
  }
  
  /**
   * Calculate treatment priority
   * @param {Object} treatment - Treatment data
   * @param {Object} detection - Detection data
   * @returns {string} Priority level
   */
  static calculatePriority(treatment, detection) {
    // Base priority from treatment or fallback
    let priority = treatment.priority || 'medium';
    
    // Adjust based on AI confidence and treatment type
    if (detection.confidence > CONFIDENCE_PRIORITY_MAP.high && 
        (treatment.isUrgent || (treatment.nameTh && treatment.nameTh.includes('รักษา')))) {
      priority = 'urgent';
    } else if (detection.confidence > CONFIDENCE_PRIORITY_MAP.medium) {
      priority = 'high';
    } else if (detection.confidence < CONFIDENCE_PRIORITY_MAP.low) {
      priority = 'low';
    }
    
    return priority;
  }

  /**
   * Calculate diagnosis priority for diagnosis-only items
   * @param {Object} diagnosis - Diagnosis data
   * @param {Object} detection - Detection data
   * @returns {string} Priority level
   */
  static calculateDiagnosisPriority(diagnosis, detection) {
    let priority = 'medium'; // Default priority
    
    // Adjust based on AI confidence and diagnosis severity
    if (detection.confidence > CONFIDENCE_PRIORITY_MAP.high) {
      // High confidence detections get higher priority
      if (diagnosis.code && diagnosis.code.includes('02')) { // Caries codes
        priority = 'urgent';
      } else if (diagnosis.code && diagnosis.code.includes('05')) { // Gingivitis codes  
        priority = 'high';
      } else {
        priority = 'high';
      }
    } else if (detection.confidence > CONFIDENCE_PRIORITY_MAP.medium) {
      priority = 'medium';
    } else if (detection.confidence < CONFIDENCE_PRIORITY_MAP.low) {
      priority = 'low';
    }
    
    // Specific K-code based priorities
    const urgentKCodes = ['K020', 'K021', 'K029']; // Caries - urgent
    const highKCodes = ['K0509', 'K0519', 'K0510']; // Gingivitis - high
    
    if (urgentKCodes.includes(diagnosis.code)) {
      priority = 'urgent';
    } else if (highKCodes.includes(diagnosis.code)) {
      priority = 'high';
    }
    
    return priority;
  }

  /**
   * Generate clinical notes for diagnosis-only items
   * @param {Object} detection - Detection data
   * @param {Object} diagnosis - Diagnosis data
   * @returns {string} Clinical notes
   */
  static generateDiagnosisClinicalNotes(detection, diagnosis) {
    const notes = [];

    notes.push(`AI Detection: ${detection.class_name} (confidence: ${(detection.confidence * 100).toFixed(1)}%)`);
    notes.push(`ICD-10 Diagnosis: ${diagnosis.nameTh || diagnosis.name} (${diagnosis.code})`);

    // Add spatial location information for multiple detections
    if (detection.box && detection.box.length >= 4) {
      const [x1, y1, x2, y2] = detection.box;
      const width = Math.abs(x2 - x1);
      const height = Math.abs(y2 - y1);
      notes.push(`📍 Location: Center at (${Math.round((x1+x2)/2)}, ${Math.round((y1+y2)/2)}), Size: ${width}×${height}px`);
    }

    if (detection.confidence < CONFIDENCE_PRIORITY_MAP.medium) {
      notes.push('⚠️ Requires clinical confirmation due to moderate AI confidence');
    }

    if (detection.photoType) {
      notes.push(`Detected in: ${detection.photoType} occlusal photo`);
    }

    // Add K-code specific notes
    if (diagnosis.code) {
      if (diagnosis.code.startsWith('K02')) {
        notes.push('📋 Dental caries detected - consider restorative treatment');
      } else if (diagnosis.code.startsWith('K05')) {
        notes.push('📋 Gingival condition detected - consider periodontal care');
      } else if (diagnosis.code.startsWith('K03')) {
        notes.push('📋 Hard tissue condition detected - evaluate for protective measures');
      }
    }

    notes.push('🔬 This is a diagnosis-only item - treatment planning required separately');
    notes.push('🗂️ Multiple detections of the same condition will appear as separate rows');

    return notes.join('\n');
  }
  
  /**
   * Generate clinical notes
   * @param {Object} detection - Detection data
   * @param {Object} diagnosis - Diagnosis data
   * @param {Object} treatment - Treatment data
   * @returns {string} Clinical notes
   */
  static generateClinicalNotes(detection, diagnosis, treatment) {
    const notes = [];
    
    notes.push(`AI detected: ${detection.class_name} (confidence: ${(detection.confidence * 100).toFixed(1)}%)`);
    notes.push(`Diagnosis: ${diagnosis.nameTh || diagnosis.name} (${diagnosis.code || 'N/A'})`);
    notes.push(`Recommended treatment: ${treatment.nameTh || treatment.name} (${treatment.code || 'N/A'})`);
    
    if (detection.confidence < CONFIDENCE_PRIORITY_MAP.medium) {
      notes.push('⚠️ Requires clinical confirmation due to moderate confidence');
    }
    
    if (detection.photoType) {
      notes.push(`Detected in: ${detection.photoType} occlusal photo`);
    }
    
    return notes.join('\n');
  }
  
  /**
   * Get fallback treatment for failed database lookups
   * @param {string} problemName - Problem name
   * @param {Object} detection - Detection data
   * @param {Object} store - Vuex store for database access
   * @returns {Object|null} Fallback treatment item
   */
  static getFallbackTreatment(problemName, detection, store = null) {
    const fallback = FALLBACK_TREATMENTS[problemName];
    if (!fallback) return null;
    
    return {
      id: `fallback_${problemName}_${Date.now()}`,
      toothNo: null,
      toothSurfaces: [],
      diagnosis: 0, // No database diagnosis
      treatment: 0, // No database treatment
      price: fallback.price,
      detail: fallback.treatment,
      status: 'Planning',
      aiGenerated: true,
      aiConfidence: detection.confidence,
      aiProblemType: detection.class_name,
      aiPhotoType: detection.photoType,
      aiBoundingBox: detection.box,
      priority: fallback.priority,
      clinicalNotes: `Fallback treatment - database lookup failed\nAI detected: ${problemName}`,
      isFallback: true
    };
  }
  
  /**
   * Generate basic treatment plan for fallback scenarios
   * @param {Object} detections - AI detections
   * @param {Object} options - Options
   * @returns {Array} Basic treatment plan
   */
  static generateFallbackTreatmentPlan(detections, options) {
    console.warn('Using fallback treatment plan generation');
    const treatmentPlan = [];
    let planId = 1;
    
    Object.keys(detections).forEach(category => {
      const categoryDetections = detections[category] || [];
      
      categoryDetections.forEach((detection, index) => {
        if (detection.confidence >= (options.minConfidence || 0.3)) {
          const fallbackTreatment = this.getFallbackTreatment(detection.class_name, detection, null);
          if (fallbackTreatment) {
            fallbackTreatment.id = `fallback_${category}_${index}`;
            fallbackTreatment.planId = planId++;
            fallbackTreatment.order = treatmentPlan.length + 1;
            treatmentPlan.push(fallbackTreatment);
          }
        }
      });
    });
    
    return treatmentPlan;
  }

  /**
   * Helper method to get diagnosis category
   * @param {number} diagnosisId - Diagnosis ID
   * @param {Object} store - Vuex store
   * @returns {number|null} Category ID
   */
  static getDiagnosisCategory(diagnosisId, store) {
    try {
      const diagnosis = store.getters['moduleAppointment/getTreatDiagBy'](diagnosisId, 'id');
      return diagnosis ? diagnosis.categoryId : null;
    } catch (error) {
      console.error('Error getting diagnosis category:', error);
      return null;
    }
  }
  
  /**
   * Optimize treatment plan by removing duplicates and sorting
   * @param {Array} treatmentPlan - Raw treatment plan
   * @param {Object} options - Options
   * @returns {Array} Optimized treatment plan
   */
  static optimizeTreatmentPlan(treatmentPlan, options) {
    // Remove duplicates based on tooth number and treatment type
    const uniqueTreatments = this.removeDuplicateTreatments(treatmentPlan);
    
    // Sort by priority and confidence
    const sortedTreatments = options.prioritizeByConfidence !== false ? 
      this.sortTreatmentsByPriority(uniqueTreatments) : uniqueTreatments;
    
    // Update order numbers after sorting
    sortedTreatments.forEach((item, index) => {
      item.order = index + 1;
    });
    
    return sortedTreatments;
  }
  
  /**
   * Remove duplicate treatments - updated to consider spatial location
   * @param {Array} treatments - Treatment list
   * @returns {Array} Unique treatments
   */
  static removeDuplicateTreatments(treatments) {
    const seen = new Set();
    return treatments.filter(treatment => {
      // For multiple detections, include spatialId in the uniqueness key
      const spatialKey = treatment.spatialId || 'no_spatial';
      const key = `${treatment.toothNo}_${treatment.treatment}_${treatment.diagnosis}_${spatialKey}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }
  
  /**
   * Sort treatments by priority and confidence
   * @param {Array} treatments - Treatment list
   * @returns {Array} Sorted treatments
   */
  static sortTreatmentsByPriority(treatments) {
    return treatments.sort((a, b) => {
      const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
      const aPriority = priorityOrder[a.priority] || 2;
      const bPriority = priorityOrder[b.priority] || 2;
      
      if (aPriority !== bPriority) {
        return bPriority - aPriority; // Higher priority first
      }
      
      return b.aiConfidence - a.aiConfidence; // Higher confidence first
    });
  }
  
  /**
   * Get AI detection keywords for debugging
   * @returns {Object} Keywords mapping
   */
  static getDetectionKeywords() {
    return AI_DETECTION_KEYWORDS;
  }
  
  /**
   * Get fallback treatments for debugging
   * @returns {Object} Fallback treatments
   */
  static getFallbackTreatments() {
    return FALLBACK_TREATMENTS;
  }

  /**
   * Validate and normalize problem name
   * @param {string} problemName - Problem name from AI
   * @returns {string} Normalized problem name
   */
  static normalizeProblemName(problemName) {
    return problemName.trim();
  }

  /**
   * Generate unique spatial identifier for multiple detections
   * @param {Object} detection - Detection data with bounding box
   * @param {string} category - Detection category
   * @param {number} detectionIndex - Index in detections array
   * @returns {string} Unique spatial identifier
   */
  static generateSpatialId(detection, category, detectionIndex) {
    if (detection.box && detection.box.length >= 4) {
      const [x1, y1, x2, y2] = detection.box;
      const centerX = Math.round((x1 + x2) / 2);
      const centerY = Math.round((y1 + y2) / 2);
      return `spatial_${centerX}_${centerY}_${detectionIndex}`;
    }
    // Fallback for detections without bounding box
    return `spatial_unknown_${detectionIndex}_${Date.now()}`;
  }

  /**
   * Generate human-readable location label for multiple detections
   * @param {Object} detection - Detection data
   * @param {number} detectionIndex - Index in detections array
   * @returns {string} Location label
   */
  static generateLocationLabel(detection, detectionIndex) {
    const areaNumber = detectionIndex + 1;

    if (detection.photoType) {
      const photoLabel = detection.photoType === 'upper' ? 'ฟันบน' : 'ฟันล่าง';
      return `(พื้นที่ ${areaNumber} - ${photoLabel})`;
    }

    return `(พื้นที่ ${areaNumber})`;
  }

  /**
   * Calculate detection area size from bounding box
   * @param {Array} boundingBox - [x1, y1, x2, y2]
   * @returns {number} Area size in pixels
   */
  static calculateDetectionArea(boundingBox) {
    if (!boundingBox || boundingBox.length < 4) {
      return 0;
    }

    const [x1, y1, x2, y2] = boundingBox;
    const width = Math.abs(x2 - x1);
    const height = Math.abs(y2 - y1);
    return Math.round(width * height);
  }

}

export default TreatmentMapping;
export { AI_DETECTION_KEYWORDS, CONFIDENCE_PRIORITY_MAP, FALLBACK_TREATMENTS };